/**
 * PATH: packages/ui/src/components/settings/email-accounts-tab/email-accounts-tab-types.ts
 * PURPOSE: Types and constants for the Email Accounts settings tab
 * WHY: Extracted from EmailAccountsTab.tsx to keep the orchestrator under 300 lines
 *
 * DEPENDENCIES:
 *   - ../types: SettingsApiClient contract
 */

import type { SettingsApiClient } from '../types'

// =============================================================================
// TYPES
// =============================================================================

export interface EmailAccountData {
  id: number
  email_address: string
  display_name: string
  provider: 'outlook' | 'gmail'
  account_type: 'personal' | 'shared'
  is_active?: boolean
  sync_enabled: boolean
  last_sync_at: string | null
  last_sync_error?: string | null
  // Per-mailbox health (backend enrichment 2026-06-16). Optional so the
  // card renders correctly BOTH before and after the backend deploy that
  // adds these fields — tolerate the lean (legacy) and enriched shapes.
  is_expired?: boolean
  needs_reconnection?: boolean
  token_expires_at?: string | null
  // Owner: the list endpoint historically sent only the flat
  // owner_name / owner_user_id; the enriched endpoint adds a nested
  // owner object. The card prefers the nested object and falls back to
  // the flat name (see resolveOwnerName) — this is why "Connected by
  // <blank>" appeared (card read owner.name; list sent owner_name).
  owner?: {
    id: number
    email: string
    name: string
  }
  owner_name?: string
  owner_user_id?: number
  // Whether THIS viewer may toggle private/shared (owner OR org admin/owner,
  // computed server-side). Absent on older backends → fall back to isOwner.
  can_manage_privacy?: boolean
  // Kanban approval gate (operator ask 2026-06-28). When true, emails from
  // this mailbox wait for manual approval before auto-filing onto a board.
  // `kanban_approval_locked` = true for a PRIVATE mailbox that already has
  // approval on (the OFF switch is disabled — switch to shared to disable).
  // Both optional so the card tolerates a pre-deploy backend (treated off).
  require_kanban_approval?: boolean
  kanban_approval_locked?: boolean
  created_at?: string
  access_grants?: {
    id: number
    user_id: number
    user_email: string
    user_name: string
    access_level: 'read' | 'send' | 'manage'
    granted_at: string
  }[]
}

export interface EmailAccountsTabProps {
  /** API client for backend calls */
  apiClient?: SettingsApiClient
  /** Organisation ID for scoped requests */
  organisationId?: string
  /** Whether current user is admin */
  isAdmin?: boolean
  /** Quotech user id of the viewer — gates the owner-only privacy toggle. */
  currentUserId?: string | null
  /** Custom className */
  className?: string
}

/** EmailAccountAccess level (mirrors the backend choices). */
export type AccessLevel = 'read' | 'send' | 'manage'

/** A pickable org member for the "share mailbox with" control. */
export interface OrgMemberOption {
  id: number
  name: string
  email: string
}

// =============================================================================
// PROVIDER CONFIG
// =============================================================================

export const PROVIDER_CONFIG = {
  outlook: {
    name: 'Microsoft Outlook',
    color: 'bg-blue-500',
    connectUrl: '/api/outlook/authorize/',
  },
  gmail: {
    name: 'Gmail',
    color: 'bg-red-500',
    connectUrl: '/api/gmail/authorize/',
  },
} as const

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Resolve a human label for the user who connected a mailbox.
 *
 * Defends the FE↔BE contract boundary: prefers the nested `owner`
 * object (enriched endpoint), falls back to the flat `owner_name`
 * (lean/legacy endpoint), then the email address — so the card never
 * renders "Connected by " with an empty name regardless of which
 * response shape the backend returns.
 */
export function resolveOwnerName(account: EmailAccountData): string {
  return (
    account.owner?.name ||
    account.owner?.email ||
    account.owner_name ||
    account.email_address
  )
}

export function formatEmailDate(dateStr: string | null): string | null {
  if (!dateStr) return null
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}
